import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import { Popup } from "@progress/kendo-react-popup";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import IconComponent from "../../components/common/IconComponent";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete, MdOutlineVisibility } from "react-icons/md";

import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { clearEmailTemplateID, setEmailTemplateID } from "./emailTemplateSlice";
import ButtonCreate from "../../components/drawer/ButtonCreate";
import ButtonEdit from "../../components/drawer/ButtonEdit";
import LinkEdit from "../../components/drawer/LinkEdit";
import {
  ActiveInactiveEmailTemplate,
  deleteEmailTemplate,
  getAllEmailTemplate,
} from "./services/emailTemplate.services";

const EmailTemplate: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllEmailTemplate());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      <DeleteEmailTemplateDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Email Template List
          </Typography.h4>
          {isAddAccess && (
            <ButtonCreate
              label="Add New Email Template"
              drawerName="emailTemplateForm"
              drawerWidth={500}
            />
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <EmailTemplateGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const EmailTemplateGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const EmailTemplateList = useAppSelector(
    (state) => state.emailTemplate.EmailTemplateList,
  );
  const loading = useAppSelector((state) => state.emailTemplate.loading);

  useEffect(() => {
    dispatch(getAllEmailTemplate());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? EmailTemplateList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteEmailTemplate"));
      dispatch(setEmailTemplateID(ID));
    };

    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <ButtonEdit
            ID={props.dataItem.ID}
            drawerName="emailTemplateForm"
            tooltipTitle="Edit"
            drawerWidth={500}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 1 || props.index === 2 || props.index === 3
          ? "locked-header"
          : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const ContentPreviewCell = (props: GridCellProps) => {
    const [show, setShow] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);

    const handleMouseEnter = () => setShow(true);
    const handleMouseLeave = () => setShow(false);

    return (
      <td
        className="table-cell"
        style={{
          ...props.style,
          verticalAlign: "middle",
        }}
      >
        <div
          ref={anchorRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            color: "#004d40",
            backgroundColor: "#e0f2f1",
            border: "1px solid #b2dfdb",
            padding: "5px 12px",
            borderRadius: "15px",
            fontSize: "12px",
            fontWeight: 600,
            transition: "all 0.2s ease",
            width: "fit-content",
          }}
        >
          <IconComponent
            icon={MdOutlineVisibility}
            style={{ fontSize: "16px", color: "#004d40" }}
          />
          <span>View Content</span>
        </div>

        <Popup
          anchor={anchorRef.current}
          show={show}
          popupClass="content-popup"
          anchorAlign={{ horizontal: "right", vertical: "bottom" }}
          popupAlign={{ horizontal: "right", vertical: "top" }}
        >
          <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
              padding: "14px",
              backgroundColor: "#ffffff",
              border: "1px solid #dcdfe6",
              borderRadius: "8px",
              boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
              width: "360px",
              maxHeight: "400px",
              overflowY: "auto",
              zIndex: 99999,
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#546e7a",
                marginBottom: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                borderBottom: "1px solid #eceff1",
                paddingBottom: "4px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Content Preview</span>
              <span
                style={{ fontSize: "10px", color: "#90a4ae", fontWeight: 400 }}
              >
                HTML
              </span>
            </div>
            <div
              style={{ fontSize: "13px", color: "#2c3e50", lineHeight: "1.5" }}
              dangerouslySetInnerHTML={{
                __html: props.dataItem?.Content || "<em>No Content</em>",
              }}
            />
          </div>
        </Popup>
      </td>
    );
  };

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(
      typeof props.dataItem.IsActive === "boolean"
        ? props.dataItem.IsActive
        : props.dataItem.IsActive === 1 || props.dataItem.IsActive === true,
    );

    const handleActiveInActive = () => {
      const newStatus = !IsActive;
      setIsActive(newStatus);
      const data = {
        ID: props.dataItem.ID,
        IsActive: newStatus,
      };
      dispatch(ActiveInactiveEmailTemplate(data)).then(() => {
        dispatch(getAllEmailTemplate());
      });
    };

    return (
      <td
        style={{ textAlign: "center", ...props.style }}
        className={props.className}
      >
        <Switch
          disabled={!isEditAccess}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {EmailTemplateList && EmailTemplateList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          filter={filter}
          className="responsive-table"
          data={
            filter
              ? filterBy(EmailTemplateList, filter).slice(
                  page.skip,
                  page.take + page.skip,
                )
              : EmailTemplateList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={EmailTemplateList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="Subject"
            title="Subject"
            cells={{
              data: (props: any) => (
                <td
                  className={`${isEditAccess ? "hoverableName" : "normalName"}`}
                >
                  {isEditAccess ? (
                    <LinkEdit
                      ID={props.dataItem.ID}
                      drawerName="emailTemplateForm"
                      drawerWidth={500}
                      label={props.dataItem?.Subject}
                    />
                  ) : (
                    props.dataItem?.Subject
                  )}
                </td>
              ),
            }}
          />
          <Column
            field="Content"
            title="Content"
            width={"180px"}
            cells={{ data: ContentPreviewCell }}
            filterable={false}
          />
          <Column
            field="ID"
            title="Status"
            cells={{ data: MyActiveInActtiveCommandCell }}
            width={"100px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"150px"}
              locked={true}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteEmailTemplateDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.emailTemplate.loading);
  const EmailTemplateID = useAppSelector(
    (state) => state.emailTemplate.EmailTemplateID,
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteEmailTemplate(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getAllEmailTemplate());
        dispatch(closeDialog());
        dispatch(clearEmailTemplateID());
      } else {
        dispatch(getAllEmailTemplate());
        dispatch(closeDialog());
        dispatch(clearEmailTemplateID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteEmailTemplate" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Email Template"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Email Template?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(EmailTemplateID)}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default EmailTemplate;
